import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useAdminAuth } from '../AdminAuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001';

const DAY_OPTIONS = [7, 30, 90];

const COUNTRY_NAMES = {
  BR: 'Brasil', US: 'Estados Unidos', PT: 'Portugal', AR: 'Argentina',
  UY: 'Uruguai', PY: 'Paraguai', BO: 'Bolívia', CL: 'Chile', PE: 'Peru',
  CO: 'Colômbia', VE: 'Venezuela', EC: 'Equador', MX: 'México',
  ES: 'Espanha', FR: 'França', DE: 'Alemanha', IT: 'Itália',
  GB: 'Reino Unido', CN: 'China', JP: 'Japão', IN: 'Índia',
};

const countryFlag = (code) => {
  if (!code || code === 'Desconhecido') return '🌐';
  return code.toUpperCase().replace(/./g, c => String.fromCodePoint(c.charCodeAt(0) + 127397));
};

const countryName = (code) => COUNTRY_NAMES[code] || code;

const COUNTRY_COORDS = {
  BR: [-14.235, -51.925], US: [37.09, -95.71], PT: [39.4, -8.22],
  AR: [-38.4, -63.6], UY: [-32.5, -55.8], PY: [-23.4, -58.4],
  BO: [-16.3, -63.6], CL: [-35.7, -71.5], PE: [-9.2, -75.0],
  CO: [4.57, -74.3], VE: [6.42, -66.6], EC: [-1.83, -78.18],
  MX: [23.6, -102.5], ES: [40.46, -3.74], FR: [46.23, 2.21],
  DE: [51.17, 10.45], IT: [41.87, 12.57], GB: [55.38, -3.44],
  CN: [35.86, 104.2], JP: [36.2, 138.25], IN: [20.59, 78.96],
  CA: [56.13, -106.35], AU: [-25.27, 133.78], ZA: [-30.56, 22.94],
  NG: [9.08, 8.68], KE: [-0.02, 37.91], AO: [-11.2, 17.87],
  RU: [61.52, 105.32], KR: [35.91, 127.77], ID: [-0.79, 113.92],
  PH: [12.88, 121.77], TH: [15.87, 100.99], VN: [14.06, 108.28],
  MY: [4.21, 108.1], SG: [1.35, 103.82], PK: [30.38, 69.35],
  BD: [23.68, 90.36], EG: [26.82, 30.8], MA: [31.79, -7.09],
  GH: [7.95, -1.02], TZ: [-6.37, 34.89], ET: [9.15, 40.49],
  UG: [1.37, 32.29], MZ: [-18.67, 35.53], ZW: [-19.02, 29.15],
  NL: [52.33, 5.29], BE: [50.5, 4.47], SE: [60.13, 18.64],
  NO: [60.47, 8.47], DK: [56.26, 9.5], FI: [61.92, 25.75],
  PL: [51.92, 19.15], CZ: [49.82, 15.47], AT: [47.52, 14.55],
  CH: [46.82, 8.23], HU: [47.16, 19.5], RO: [45.94, 24.97],
  GR: [39.07, 21.82], TR: [38.96, 35.24], IL: [31.05, 34.85],
  SA: [23.89, 45.08], AE: [23.42, 53.85], IR: [32.43, 53.69],
  IQ: [33.22, 43.68], UA: [48.38, 31.17], BY: [53.71, 27.95],
  NZ: [-40.9, 174.88], GT: [15.78, -90.23],
  HN: [15.2, -86.24], SV: [13.79, -88.9], NI: [12.86, -85.21],
  CR: [9.75, -83.75], PA: [8.54, -80.78], CU: [21.52, -79.02],
  DO: [18.74, -70.16], HT: [18.97, -72.29], JM: [18.11, -77.3],
  TT: [10.69, -61.22], PR: [18.22, -66.59],
};

const AccessMap = ({ byCountry }) => {
  const points = (byCountry || [])
    .filter(c => c.country && c.country !== 'Desconhecido' && COUNTRY_COORDS[c.country])
    .map(c => ({ ...c, coords: COUNTRY_COORDS[c.country], views: Number(c.views) }));

  const maxViews = Math.max(...points.map(p => p.views), 1);

  return (
    <>
      <style>{`
        .leaflet-control-attribution { display: none !important; }
        .analytics-map-tooltip { font-size: 12px; font-weight: 600; }
      `}</style>
      <div style={{ height: 320, width: '100%', borderRadius: 12, overflow: 'hidden', position: 'relative', zIndex: 0 }}>
        <MapContainer
          center={[20, 0]}
          zoom={2}
          minZoom={1}
          maxZoom={5}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
          attributionControl={false}
        >
          <TileLayer url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png" />
          {points.map((p, i) => {
            const radius = 6 + Math.round((p.views / maxViews) * 22);
            return (
              <CircleMarker
                key={i}
                center={p.coords}
                radius={radius}
                pathOptions={{ color: '#1B4F8A', weight: 1.5, fillColor: '#1B4F8A', fillOpacity: 0.55 }}
              >
                <Tooltip className="analytics-map-tooltip">
                  {countryFlag(p.country)} {countryName(p.country)}: {p.views} acessos
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </>
  );
};

const StatCard = ({ label, value, icon, color }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-gray-500 font-medium">{label}</span>
      <span className="material-icons text-2xl" style={{ color }}>{icon}</span>
    </div>
    <p className="text-3xl font-extrabold text-gray-900">{value ?? '—'}</p>
  </div>
);

const BAR_MAX_H = 120;
const BAR_MIN_H = 4;

const BarChart = ({ data }) => {
  if (!data || data.length === 0) return <p className="text-sm text-gray-400 py-8 text-center">Sem dados</p>;
  const max = Math.max(...data.map(d => Number(d.views)), 1);
  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-end gap-1 pb-2" style={{ height: BAR_MAX_H + 32, minWidth: data.length * 24 }}>
        {data.map((d, i) => {
          const barH = Math.max(Math.round((Number(d.views) / max) * BAR_MAX_H), BAR_MIN_H);
          const rawDate = d.date ? String(d.date).substring(0, 10) : '';
          const label = rawDate ? new Date(rawDate + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';
          return (
            <div key={i} className="flex flex-col items-center flex-1 min-w-[24px] group relative" style={{ height: BAR_MAX_H + 24 }}>
              <div style={{ flex: 1 }} />
              <div
                className="w-full rounded-t-md transition-all duration-300 cursor-default"
                style={{ height: barH, background: '#1B4F8A' }}
              />
              <span className="text-[8px] text-gray-400 mt-1 whitespace-nowrap"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', maxHeight: 28, overflow: 'hidden' }}>
                {label}
              </span>
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden group-hover:flex bg-gray-900 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-10">
                {label}: {d.views} views
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HBarList = ({ items, labelKey, color }) => {
  if (!items || items.length === 0) return <p className="text-sm text-gray-400">Sem dados</p>;
  const max = Number(items[0]?.views) || 1;
  return (
    <ul className="space-y-3">
      {items.map((r, i) => {
        const pct = Math.round((Number(r.views) / max) * 100);
        return (
          <li key={i}>
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span className="truncate max-w-[160px]" title={r[labelKey]}>{r[labelKey]}</span>
              <span className="font-semibold ml-2 flex-shrink-0">{r.views}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const AnalyticsPage = () => {
  const { token } = useAdminAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/analytics/summary?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Falha ao carregar dados');
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days, token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900">Analytics</h2>
          <p className="text-sm text-gray-500 mt-0.5">Acessos ao site</p>
        </div>
        <div className="flex gap-2">
          {DAY_OPTIONS.map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${days === d ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
              {d} dias
            </button>
          ))}
          <button onClick={load} className="px-3 py-2 rounded-xl border-2 border-gray-200 text-gray-500 hover:border-gray-300 transition-all" title="Atualizar">
            <span className="material-icons text-lg">refresh</span>
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-24">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total de visualizações" value={data.total?.toLocaleString('pt-BR')} icon="visibility" color="#1B4F8A" />
            <StatCard label="Visitantes únicos" value={data.unique_visitors?.toLocaleString('pt-BR')} icon="person" color="#F47920" />
            <StatCard label="Países distintos" value={data.by_country?.filter(c => c.country !== 'Desconhecido').length || 0} icon="public" color="#10b981" />
            <StatCard label="Período (dias)" value={data.days} icon="date_range" color="#8b5cf6" />
          </div>

          {/* World access map */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Mapa de acessos</h3>
            <AccessMap byCountry={data.by_country} />
          </div>

          {/* Day chart + referrers */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-sm font-bold text-gray-700 mb-4">Visualizações por dia</h3>
              <BarChart data={data.by_day} />
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-sm font-bold text-gray-700 mb-4">Origens de tráfego</h3>
              <HBarList items={data.by_referrer} labelKey="referrer" color="#F47920" />
            </div>
          </div>

          {/* Countries + Cities */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-sm font-bold text-gray-700 mb-4">Países</h3>
              {!data.by_country?.length ? (
                <p className="text-sm text-gray-400">Sem dados</p>
              ) : (
                <ul className="space-y-2">
                  {data.by_country.map((c, i) => {
                    const max = Number(data.by_country[0]?.views) || 1;
                    const pct = Math.round((Number(c.views) / max) * 100);
                    return (
                      <li key={i}>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span className="flex items-center gap-2">
                            <span className="text-base leading-none">{countryFlag(c.country === 'Desconhecido' ? null : c.country)}</span>
                            <span>{c.country === 'Desconhecido' ? 'Desconhecido' : countryName(c.country)}</span>
                          </span>
                          <span className="font-semibold">{c.views}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: '#1B4F8A' }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-sm font-bold text-gray-700 mb-4">Cidades</h3>
              {!data.by_city?.length ? (
                <p className="text-sm text-gray-400">Sem dados</p>
              ) : (
                <ul className="space-y-2">
                  {data.by_city.map((c, i) => {
                    const max = Number(data.by_city[0]?.views) || 1;
                    const pct = Math.round((Number(c.views) / max) * 100);
                    const label = c.city === 'Desconhecida'
                      ? 'Desconhecida'
                      : `${c.city}${c.country ? ` · ${countryFlag(c.country)}` : ''}`;
                    return (
                      <li key={i}>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span className="truncate max-w-[180px]">{label}</span>
                          <span className="font-semibold ml-2">{c.views}</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: '#10b981' }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Top pages */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-700">Páginas mais acessadas</h3>
            </div>
            {!data.by_path?.length ? (
              <p className="text-sm text-gray-400 px-6 py-4">Sem dados</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Página</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Visualizações</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">% do total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.by_path.map((p, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 text-gray-400 font-mono">{i + 1}</td>
                      <td className="px-6 py-3 text-gray-800 font-medium">{p.path}</td>
                      <td className="px-6 py-3 text-right text-gray-700 font-semibold">{Number(p.views).toLocaleString('pt-BR')}</td>
                      <td className="px-6 py-3 text-right text-gray-500">
                        {data.total > 0 ? Math.round((Number(p.views) / data.total) * 100) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
